// Gestiona historial de ultimas busquedas y su persistencia en localStorage.
const createLastSearchesService = (host) => {
  const service = {
    getStoredSearches() {
      const storageKey = host.props.lastSearches?.storageKey;
      if (!storageKey) {
        return [];
      }

      if (Array.isArray(host.props.lastSearches.items) && host.props.lastSearches.items.length) {
        const cachedItems = service.normalizeAndPruneSearches(host.props.lastSearches.items);
        if (cachedItems.length !== host.props.lastSearches.items.length) {
          host.props.lastSearches.items = cachedItems;
          service.persistSearches(cachedItems);
        }

        return cachedItems;
      }

      try {
        const rawValue = localStorage.getItem(storageKey);
        if (!rawValue) {
          host.props.lastSearches.items = [];
          return [];
        }

        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) {
          host.props.lastSearches.items = [];
          return [];
        }

        const normalized = service.normalizeAndPruneSearches(parsed);

        host.props.lastSearches.items = normalized;
        if (normalized.length !== parsed.length) {
          service.persistSearches(normalized);
        }

        return normalized;
      } catch (error) {
        console.error("Error reading recent searches from storage", error);
        host.props.lastSearches.items = [];
        return [];
      }
    },

    normalizeSearchEntry(entry) {
      if (!entry || typeof entry !== "object") {
        return null;
      }

      const destination = service.normalizeValue(entry.destination);
      const assistants = service.normalizeValue(entry.assistants);
      const eventType = service.normalizeValue(entry.eventType);
      const destinationPath = service.normalizeValue(entry.destinationPath);
      const capacityKey = service.normalizeValue(entry.capacityKey);
      const eventTypeKey = service.normalizeValue(entry.eventTypeKey);

      if (!destination || !assistants || !eventType) {
        return null;
      }

      const fallbackId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      return {
        id: service.normalizeValue(entry.id) || fallbackId,
        destination,
        assistants,
        eventType,
        destinationPath,
        capacityKey,
        eventTypeKey,
        timestamp: Number.isFinite(entry.timestamp) ? entry.timestamp : Date.now(),
      };
    },

    normalizeValue(value) {
      return String(value || "").trim();
    },

    normalizeForMatch(value) {
      return String(value || "")
        .normalize("NFD")
        .replaceAll(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },

    getSearchTtlMs() {
      const configuredTtl = Number(host.props.lastSearches?.ttlMs);
      if (Number.isFinite(configuredTtl) && configuredTtl > 0) {
        return configuredTtl;
      }

      return 30 * 24 * 60 * 60 * 1000;
    },

    isExpiredSearch(entry, now = Date.now()) {
      const timestamp = Number(entry?.timestamp);
      if (!Number.isFinite(timestamp)) {
        return false;
      }

      return now - timestamp > service.getSearchTtlMs();
    },

    normalizeAndPruneSearches(items) {
      if (!Array.isArray(items)) {
        return [];
      }

      const now = Date.now();
      return items
        .map(service.normalizeSearchEntry)
        .filter(Boolean)
        .filter((item) => !service.isExpiredSearch(item, now))
        .slice(-(host.props.lastSearches?.maxItems || 3));
    },

    setSelectedResultPath(fastbooking, path = "") {
      const normalizedPath = service.normalizeValue(path);
      if (!fastbooking) {
        return;
      }

      if (!normalizedPath) {
        delete fastbooking.dataset.selectedResultPath;
        return;
      }

      fastbooking.dataset.selectedResultPath = normalizedPath;
    },

    resolveSelectedPathByLabel(fastbooking, destinationLabel) {
      const normalizedDestination = service.normalizeForMatch(destinationLabel);
      if (!fastbooking || !normalizedDestination) {
        return "";
      }

      const hotelItems = Array.from(fastbooking.querySelectorAll(`${host.props.accordions.label}[data-page-path]`));
      for (const hotelItem of hotelItems) {
        const hotelLabel = service.normalizeForMatch(hotelItem.dataset.text || hotelItem.textContent);
        if (hotelLabel === normalizedDestination) {
          return service.normalizeValue(hotelItem.dataset.pagePath);
        }
      }

      const destinationItems = Array.from(fastbooking.querySelectorAll(`${host.props.accordions.label}[data-destination-path]`));
      for (const destinationItem of destinationItems) {
        const textNode = destinationItem.querySelector(host.props.accordions.text);
        const destinationText = service.normalizeForMatch(textNode?.dataset?.text || textNode?.textContent || destinationItem.textContent);

        if (destinationText === normalizedDestination) {
          return service.normalizeValue(destinationItem.dataset.destinationPath);
        }
      }

      return "";
    },

    resolveFacetKeyFromLabel(label) {
      const normalizedLabel = service.normalizeValue(label);
      if (!normalizedLabel) {
        return "";
      }

      const staticLabels = host.props.staticMeetingOptions?.labels || {};
      const normalizedTarget = service.normalizeForMatch(normalizedLabel);
      const matchedEntry = Object.entries(staticLabels).find(([, literal]) => service.normalizeForMatch(literal) === normalizedTarget);

      return matchedEntry ? matchedEntry[0] : normalizedLabel;
    },

    persistSearches(items) {
      const storageKey = host.props.lastSearches?.storageKey;
      if (!storageKey) {
        return;
      }

      try {
        localStorage.setItem(storageKey, JSON.stringify(items));
      } catch (error) {
        console.error("Error writing recent searches to storage", error);
      }
    },

    saveCurrentSearch(payload) {
      const normalized = service.normalizeSearchEntry(payload);
      if (!normalized) {
        return null;
      }

      const maxItems = host.props.lastSearches?.maxItems || 3;
      const currentItems = service.getStoredSearches();
      const nextItems = [...currentItems, normalized].slice(-maxItems);

      host.props.lastSearches.items = nextItems;
      service.persistSearches(nextItems);

      return normalized;
    },

    setForceDestinations(fastbooking, shouldForce) {
      if (!fastbooking) {
        return;
      }

      const attr = host.props.lastSearches.forceDestinationsDataAttr;
      fastbooking.dataset[attr] = shouldForce ? "true" : "false";
    },

    shouldForceDestinations(fastbooking) {
      if (!fastbooking) {
        return false;
      }

      const attr = host.props.lastSearches.forceDestinationsDataAttr;
      return fastbooking.dataset[attr] === "true";
    },

    getRecentElements(fastbooking) {
      if (!fastbooking) {
        return {};
      }

      const defaultModal = fastbooking.querySelector(host.props.modals.destinations.default);
      const firstBlock = defaultModal?.querySelector(`.${host.props.modifiers.blocks.firstBlock}`);
      const recentPanel = firstBlock?.querySelector(host.props.lastSearches.panel);
      const recentList = recentPanel?.querySelector(host.props.lastSearches.list);
      const showDestinationsBtn = recentPanel?.querySelector(host.props.lastSearches.showDestinationsBtn);
      const destinationTree = firstBlock?.querySelector(host.props.lastSearches.destinationTree);
      const firstBlockHeaderText = firstBlock?.querySelector(host.props.lastSearches.firstBlockHeaderText);
      const emptyModal = fastbooking.querySelector(host.props.modals.destinations.empty);

      return {
        defaultModal,
        emptyModal,
        firstBlock,
        recentPanel,
        recentList,
        showDestinationsBtn,
        destinationTree,
        firstBlockHeaderText,
      };
    },

    createRecentSearchNode(item, labels = {}) {
      const assistantsLabel = labels.assistantsLabel || "Asistentes";
      const eventTypeLabel = labels.eventTypeLabel || "Tipo de evento";

      const row = document.createElement("li");
      row.className = "c-fastbooking-lite__form__modal__content__item fb-lite-recentSearchRow-JS";

      const action = document.createElement("button");
      action.type = "button";
      action.className = "fb-lite-recentSearchItem-JS";
      action.dataset.searchId = item.id;

      const destination = document.createElement("p");
      destination.className = "history__hotel-name fb-lite-recentDestination-JS";
      destination.textContent = item.destination;

      const features = document.createElement("div");
      features.className = "history__hotel-features";

      const assistants = document.createElement("span");
      assistants.className = "fb-lite-recent-field";
      const assistantsLabelNode = document.createElement("span");
      assistantsLabelNode.className = "fb-lite-recent-field__label";
      assistantsLabelNode.textContent = `${assistantsLabel}:`;
      const assistantsValueNode = document.createElement("span");
      assistantsValueNode.className = "fb-lite-recentAssistants-JS";
      assistantsValueNode.textContent = item.assistants;
      assistants.append(assistantsLabelNode, assistantsValueNode);

      const eventType = document.createElement("span");
      eventType.className = "fb-lite-recent-field";
      const eventTypeLabelNode = document.createElement("span");
      eventTypeLabelNode.className = "fb-lite-recent-field__label";
      eventTypeLabelNode.textContent = `${eventTypeLabel}:`;
      const eventTypeValueNode = document.createElement("span");
      eventTypeValueNode.className = "fb-lite-recentEventType-JS";
      eventTypeValueNode.textContent = item.eventType;
      eventType.append(eventTypeLabelNode, eventTypeValueNode);

      features.append(assistants, eventType);
      action.append(destination, features);
      row.append(action);

      return row;
    },

    ensureEventsBound(fastbooking, recentList, showDestinationsBtn) {
      if (recentList && recentList.dataset.fastbookingBound !== "true") {
        recentList.dataset.fastbookingBound = "true";

        recentList.addEventListener("click", (evt) => {
          const itemNode = evt.target.closest(".fb-lite-recentSearchItem-JS");
          if (!itemNode?.dataset.searchId) {
            return;
          }

          host.applyRecentSearch(fastbooking, itemNode.dataset.searchId);
        });
      }

      if (showDestinationsBtn && showDestinationsBtn.dataset.fastbookingBound !== "true") {
        showDestinationsBtn.dataset.fastbookingBound = "true";
        showDestinationsBtn.addEventListener("click", (evt) => {
          evt.preventDefault();
          host.showAllDestinationsFromRecent(fastbooking);
        });
      }
    },

    renderRecentSearches(fastbooking) {
      const { defaultModal, recentList, showDestinationsBtn } = service.getRecentElements(fastbooking);
      if (!recentList) {
        return;
      }

      const items = service.getStoredSearches();
      const sortedItems = [...items].reverse();
      const labels = {
        assistantsLabel: defaultModal?.dataset.assistantsLabel,
        eventTypeLabel: defaultModal?.dataset.eventTypeLabel,
      };

      if (showDestinationsBtn && defaultModal?.dataset.showAllDestinationsLabel) {
        showDestinationsBtn.textContent = defaultModal.dataset.showAllDestinationsLabel;
      }

      recentList.innerHTML = "";

      sortedItems.forEach((item) => {
        recentList.appendChild(service.createRecentSearchNode(item, labels));
      });

      service.ensureEventsBound(fastbooking, recentList, showDestinationsBtn);
    },

    applyRecentSearch(fastbooking, searchId) {
      const item = service.getStoredSearches().find((storedItem) => storedItem.id === searchId);
      if (!item || !fastbooking) {
        return null;
      }

      const destinationInput = fastbooking.querySelector(host.props.form.inputDestination);
      const closeSearchBtn = fastbooking.querySelector(host.props.btns.closeSearch);
      const selectsValues = Array.from(fastbooking.querySelectorAll(`${host.props.form.selects}:not(.${host.props.modifiers.hide}) ${host.props.form.selectsValue}`));

      if (destinationInput) {
        destinationInput.value = item.destination;
      }

      const selectedPath = service.normalizeValue(item.destinationPath) || service.resolveSelectedPathByLabel(fastbooking, item.destination);
      service.setSelectedResultPath(fastbooking, selectedPath);

      if (selectsValues[0]) {
        selectsValues[0].textContent = item.assistants;
        selectsValues[0].dataset.value = service.normalizeValue(item.capacityKey) || service.resolveFacetKeyFromLabel(item.assistants);
      }

      if (selectsValues[1]) {
        selectsValues[1].textContent = item.eventType;
        selectsValues[1].dataset.value = service.normalizeValue(item.eventTypeKey) || service.resolveFacetKeyFromLabel(item.eventType);
      }

      service.setForceDestinations(fastbooking, true);
      closeSearchBtn?.classList.add(host.props.modifiers.active);

      host.updateRecentSearchesView(fastbooking, item.destination);

      return item;
    },

    showAllDestinationsFromRecent(fastbooking) {
      if (!fastbooking) {
        return;
      }

      service.setForceDestinations(fastbooking, true);
      host.updateRecentSearchesView(fastbooking, "");

      const focusFirstDestination = () => {
        const uiService = typeof host.getUiService === "function" ? host.getUiService() : null;
        uiService?.focusFirstDestinationModalItem?.(fastbooking);
      };

      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(focusFirstDestination);
      } else {
        setTimeout(focusFirstDestination, 0);
      }
    },

    resetRecentSearchesMode(fastbooking) {
      service.setForceDestinations(fastbooking, false);
    },

    updateRecentSearchesView(fastbooking, query = "") {
      if (!fastbooking) {
        return;
      }

      const { defaultModal, emptyModal, recentPanel, recentList, destinationTree, firstBlockHeaderText, showDestinationsBtn } = service.getRecentElements(fastbooking);

      if (!defaultModal || !recentPanel || !destinationTree) {
        return;
      }

      const hasRecentSearches = service.getStoredSearches().length > 0;
      const hasQuery = Boolean(String(query || "").trim());
      const forceDestinations = service.shouldForceDestinations(fastbooking);
      const showRecent = hasRecentSearches && !hasQuery && !forceDestinations;

      defaultModal.classList.add(host.props.modifiers.show);

      if (hasQuery) {
        emptyModal?.classList.remove(host.props.modifiers.show);
      }

      if (showRecent) {
        emptyModal?.classList.remove(host.props.modifiers.show);
      }

      recentPanel.classList.toggle(host.props.modifiers.hide, !showRecent);
      recentList?.classList.toggle(host.props.modifiers.show, showRecent);
      recentList?.classList.toggle(host.props.modifiers.hide, !showRecent);
      destinationTree.classList.toggle(host.props.modifiers.hide, showRecent);
      destinationTree.classList.toggle(host.props.modifiers.show, !showRecent);
      showDestinationsBtn?.classList.toggle(host.props.modifiers.hide, !showRecent);

      if (firstBlockHeaderText) {
        const lastSearchesTitle = defaultModal.dataset.lastSearchesTitle || defaultModal.dataset.recommendedDestinations;
        firstBlockHeaderText.textContent = showRecent ? lastSearchesTitle : defaultModal.dataset.recommendedDestinations;
      }
    },
  };

  return service;
};

export default createLastSearchesService;
